import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  ChatBubbleLeftRightIcon,
  UsersIcon,
  LinkIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { marketplaceApi, WhatsAppGroup } from '../../api/marketplace';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

export default function WhatsAppGroupsManager() {
  const { user } = useAuth();
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<WhatsAppGroup | null>(null);
  const [formData, setFormData] = useState({
    groupName: '',
    description: '',
    rules: '',
    inviteLink: '',
  });

  const facilityId = 'default'; // Use a default facility ID, backend will handle facility resolution

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const groupsData = await marketplaceApi.getWhatsAppGroups(facilityId);
      setGroups(groupsData);
    } catch (error) {
              console.error('Error loading WhatsApp groups:', error);
        toast.error('Failed to load WhatsApp groups');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.groupName.trim()) {
      toast.error('Group name is required');
      return;
    }

    try {
      if (editingGroup) {
        // Update existing group
        await marketplaceApi.updateWhatsAppGroup(facilityId, editingGroup.id, formData);
        toast.success('Group updated successfully!');
      } else {
        // Create new group
                  await marketplaceApi.createWhatsAppGroup(facilityId, formData);
        toast.success('Group created successfully!');
      }
      
      setShowCreateModal(false);
      setEditingGroup(null);
      resetForm();
      loadGroups();
    } catch (error) {
      console.error('Error saving group:', error);
      toast.error('Failed to save group');
    }
  };

  const handleEdit = (group: WhatsAppGroup) => {
    setEditingGroup(group);
    setFormData({
      groupName: group.groupName,
      description: group.description || '',
      rules: group.rules || '',
      inviteLink: group.inviteLink || '',
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (groupId: string) => {
    if (!confirm('Are you sure you want to delete this group?')) {
      return;
    }

    try {
              await marketplaceApi.deleteWhatsAppGroup(facilityId, groupId);
      toast.success('Group deleted successfully!');
      loadGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    }
  };

  const resetForm = () => {
    setFormData({
      groupName: '',
      description: '',
      rules: '',
      inviteLink: '',
    });
  };

  const openModal = () => {
    setEditingGroup(null);
    resetForm();
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingGroup(null);
    resetForm();
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800';
  };

  const getStatusIcon = (isActive: boolean) => {
    return isActive ? (
      <CheckCircleIcon className="h-4 w-4" />
    ) : (
      <XCircleIcon className="h-4 w-4" />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">WhatsApp Groups</h2>
          <p className="mt-1 text-sm text-slate-500">
            Manage your WhatsApp groups for marketplace listings
          </p>
        </div>
        <button
          onClick={openModal}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Group
        </button>
      </div>

      {/* Groups Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.map((group) => (
          <div key={group.id} className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-green-600" />
                  <h3 className="text-lg font-medium text-slate-900">{group.groupName}</h3>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(group.isActive)}`}>
                  {getStatusIcon(group.isActive)}
                  <span className="ml-1">{group.isActive ? 'Active' : 'Inactive'}</span>
                </span>
              </div>

              {group.description && (
                <p className="text-sm text-slate-600 mb-4">{group.description}</p>
              )}

              <div className="flex items-center space-x-4 text-sm text-slate-500 mb-4">
                <div className="flex items-center">
                  <UsersIcon className="h-4 w-4 mr-1" />
                  <span>{group.memberCount} members</span>
                </div>
                {group.lastActivityAt && (
                  <div>
                    Last activity: {new Date(group.lastActivityAt).toLocaleDateString()}
                  </div>
                )}
              </div>

              {group.inviteLink && (
                <div className="mb-4">
                  <a
                    href={group.inviteLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-cyan-600 hover:text-cyan-800"
                  >
                    <LinkIcon className="h-4 w-4 mr-1" />
                    Join Group
                  </a>
                </div>
              )}

              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(group)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(group.id)}
                  className="inline-flex items-center justify-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {groups.length === 0 && (
        <div className="text-center py-12">
          <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-slate-400" />
                                      <h3 className="mt-2 text-sm font-medium text-slate-900">No WhatsApp groups</h3>
            <p className="mt-1 text-sm text-slate-500">
              Get started by creating your first WhatsApp group for marketplace listings.
          </p>
          <div className="mt-6">
            <button
              onClick={openModal}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Group
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <BrandModal open={showCreateModal} onClose={closeModal} maxWidthClass="max-w-md">
        <BrandModalHeader
          title={editingGroup ? 'Edit WhatsApp Group' : 'Create WhatsApp Group'}
          onClose={closeModal}
        />
        <form onSubmit={handleSubmit}>
          <BrandModalBody>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Group Name *
                  </label>
                  <input
                    type="text"
                    value={formData.groupName}
                    onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter group name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe the group's purpose..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Group Rules
                  </label>
                  <textarea
                    value={formData.rules}
                    onChange={(e) => setFormData({ ...formData, rules: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter group rules..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Invite Link
                  </label>
                  <input
                    type="url"
                    value={formData.inviteLink}
                    onChange={(e) => setFormData({ ...formData, inviteLink: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://chat.whatsapp.com/..."
                  />
                </div>

              </div>
          </BrandModalBody>
          <BrandModalFooter>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-secondary text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary text-sm"
                  >
                    {editingGroup ? 'Update Group' : 'Create Group'}
                  </button>
          </BrandModalFooter>
        </form>
      </BrandModal>
    </div>
  );
}
